import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetApiIntegrationsPhilipsHueBridges,
  usePostApiIntegrationsPhilipsHueBridges,
  usePutApiIntegrationsPhilipsHueBridgesId,
  useDeleteApiIntegrationsPhilipsHueBridgesId,
  usePostApiIntegrationsPhilipsHueBridgesIdPause,
  usePostApiIntegrationsPhilipsHueBridgesIdResume,
  usePostApiIntegrationsPhilipsHueBridgesIdDiscover,
  useGetApiIntegrationsPhilipsHueBridgesIdDevices,
  usePostApiIntegrationsPhilipsHueDevices,
  useDeleteApiIntegrationsPhilipsHueDevicesId,
  getGetApiIntegrationsPhilipsHueBridgesQueryKey,
  getGetApiIntegrationsPhilipsHueBridgesIdDevicesQueryKey,
  type HueBridge,
  type HueDevice,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  AlertCircle,
  Plus,
  Router,
  Pencil,
  Trash2,
  Pause,
  Play,
  Power,
  Search,
  ChevronRight,
  Lightbulb,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button, Modal, TextField } from '@/components/ui';

const addBridgeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  host: z.string().min(1, 'IP address is required').ip({ message: 'Must be a valid IP address' }),
  port: z.coerce
    .number({ invalid_type_error: 'Port must be a number' })
    .min(1, 'Port must be at least 1')
    .max(65535, 'Port must be at most 65535'),
});

const editBridgeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  host: z.string().min(1, 'IP address is required').ip({ message: 'Must be a valid IP address' }),
  port: z.coerce
    .number({ invalid_type_error: 'Port must be a number' })
    .min(1, 'Port must be at least 1')
    .max(65535, 'Port must be at most 65535'),
});

const enableDeviceSchema = z.object({
  humanId: z
    .string()
    .min(1, 'ID is required')
    .regex(/^[a-z0-9.-]+$/, 'ID must be lowercase letters, numbers, hyphens, and periods only'),
  name: z.string().min(1, 'Name is required'),
  deviceType: z.enum(['light', 'switch'], { required_error: 'Device type is required' }),
});

type AddBridgeFormData = z.infer<typeof addBridgeSchema>;
type EditBridgeFormData = z.infer<typeof editBridgeSchema>;
type EnableDeviceFormData = z.infer<typeof enableDeviceSchema>;

export function HueSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetApiIntegrationsPhilipsHueBridges();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBridge, setEditingBridge] = useState<HueBridge | null>(null);
  const [deletingBridge, setDeletingBridge] = useState<HueBridge | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [authPending, setAuthPending] = useState<{
    name: string;
    host: string;
    port: number;
    message: string;
    timeoutSeconds?: number;
  } | null>(null);
  const [expandedBridges, setExpandedBridges] = useState<Set<string>>(new Set());

  const addMutation = usePostApiIntegrationsPhilipsHueBridges();
  const updateMutation = usePutApiIntegrationsPhilipsHueBridgesId();
  const deleteMutation = useDeleteApiIntegrationsPhilipsHueBridgesId();
  const pauseMutation = usePostApiIntegrationsPhilipsHueBridgesIdPause();
  const resumeMutation = usePostApiIntegrationsPhilipsHueBridgesIdResume();

  const bridges = data?.bridges ?? [];

  const invalidateBridges = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsPhilipsHueBridgesQueryKey() });
  };

  const toggleBridgeExpanded = (bridgeId: string) => {
    setExpandedBridges((prev) => {
      const next = new Set(prev);
      if (next.has(bridgeId)) {
        next.delete(bridgeId);
      } else {
        next.add(bridgeId);
      }
      return next;
    });
  };

  const handlePauseResume = async (bridge: HueBridge) => {
    if (!bridge.id) return;
    try {
      if (bridge.paused) {
        await resumeMutation.mutateAsync({ id: bridge.id });
      } else {
        await pauseMutation.mutateAsync({ id: bridge.id });
      }
      invalidateBridges();
    } catch {
      // Error handling done by mutation
    }
  };

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Philips Hue
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage your Philips Hue bridge connections and devices
            </p>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
            Add Bridge
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch bridges</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your bridge list.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Bridge List */}
      {!isLoading && !isError && (
        <>
          {bridges.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Router className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No bridges configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                Add your first Philips Hue bridge to get started
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Add Bridge
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {bridges.map((bridge) => (
                <BridgeCard
                  key={bridge.id}
                  bridge={bridge}
                  isExpanded={expandedBridges.has(bridge.id ?? '')}
                  onToggleExpand={() => bridge.id && toggleBridgeExpanded(bridge.id)}
                  onEdit={() => setEditingBridge(bridge)}
                  onDelete={() => setDeletingBridge(bridge)}
                  onPauseResume={() => handlePauseResume(bridge)}
                  isPauseLoading={
                    (pauseMutation.isPending || resumeMutation.isPending) &&
                    (pauseMutation.variables?.id === bridge.id ||
                      resumeMutation.variables?.id === bridge.id)
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Bridge Modal */}
      <AddBridgeModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddError(null);
          setAuthPending(null);
        }}
        onSubmit={async (data) => {
          setAddError(null);
          try {
            const result = await addMutation.mutateAsync({ data });
            if (result.requiresAuthentication && result.authenticationStatus) {
              setAuthPending({
                name: data.name,
                host: data.host,
                port: data.port,
                message:
                  result.authenticationStatus.message ??
                  'Press the button on your Hue bridge to authenticate',
                timeoutSeconds: result.authenticationStatus.timeoutSeconds,
              });
            } else {
              setShowAddModal(false);
              setAuthPending(null);
              invalidateBridges();
            }
          } catch (err) {
            const error = err as { code?: string; message?: string };
            if (error.code === 'BRIDGE_UNREACHABLE') {
              setAddError(
                'Could not connect to the bridge. Please verify the IP address and port are correct and the bridge is powered on.'
              );
            } else if (error.code === 'BRIDGE_ALREADY_EXISTS') {
              setAddError(error.message ?? 'A bridge with this configuration already exists.');
            } else {
              setAddError('Failed to add bridge. Please try again.');
            }
          }
        }}
        onRetryAuth={async () => {
          if (!authPending) return;
          setAddError(null);
          try {
            const result = await addMutation.mutateAsync({
              data: {
                name: authPending.name,
                host: authPending.host,
                port: authPending.port,
              },
            });
            if (result.requiresAuthentication && result.authenticationStatus) {
              setAuthPending({
                ...authPending,
                message:
                  result.authenticationStatus.message ??
                  'Press the button on your Hue bridge to authenticate',
                timeoutSeconds: result.authenticationStatus.timeoutSeconds,
              });
            } else {
              setShowAddModal(false);
              setAuthPending(null);
              invalidateBridges();
            }
          } catch (err) {
            const error = err as { code?: string; message?: string };
            setAddError(error.message ?? 'Authentication failed. Please try again.');
          }
        }}
        isLoading={addMutation.isPending}
        error={addError}
        authPending={authPending}
      />

      {/* Edit Bridge Modal */}
      {editingBridge && (
        <EditBridgeModal
          isOpen={true}
          bridge={editingBridge}
          onClose={() => setEditingBridge(null)}
          onSubmit={async (data) => {
            if (!editingBridge.id) return;
            try {
              await updateMutation.mutateAsync({ id: editingBridge.id, data });
              setEditingBridge(null);
              invalidateBridges();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingBridge && (
        <DeleteBridgeModal
          isOpen={true}
          bridge={deletingBridge}
          onClose={() => setDeletingBridge(null)}
          onConfirm={async () => {
            if (!deletingBridge.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingBridge.id });
              setDeletingBridge(null);
              invalidateBridges();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

interface BridgeCardProps {
  bridge: HueBridge;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onPauseResume: () => void;
  isPauseLoading: boolean;
}

function BridgeCard({
  bridge,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onPauseResume,
  isPauseLoading,
}: BridgeCardProps) {
  const queryClient = useQueryClient();
  const discoverMutation = usePostApiIntegrationsPhilipsHueBridgesIdDiscover();

  const { data: devicesData, isLoading: devicesLoading } =
    useGetApiIntegrationsPhilipsHueBridgesIdDevices(bridge.id ?? '', {
      query: { enabled: isExpanded && !!bridge.id },
    });

  const [enablingDevice, setEnablingDevice] = useState<HueDevice | null>(null);
  const [disablingDevice, setDisablingDevice] = useState<HueDevice | null>(null);
  const [discoveredDevices, setDiscoveredDevices] = useState<HueDevice[]>([]);
  const [showDiscoveredDevices, setShowDiscoveredDevices] = useState(false);

  const enabledDevices = devicesData?.devices ?? [];

  const invalidateDevices = () => {
    if (bridge.id) {
      queryClient.invalidateQueries({
        queryKey: getGetApiIntegrationsPhilipsHueBridgesIdDevicesQueryKey(bridge.id),
      });
    }
  };

  const handleDiscover = async () => {
    if (!bridge.id) return;
    try {
      const result = await discoverMutation.mutateAsync({ id: bridge.id });
      setDiscoveredDevices(result.devices ?? []);
      setShowDiscoveredDevices(true);
    } catch {
      // Error handling done by mutation
    }
  };

  const getStatusColor = () => {
    if (bridge.paused) return 'bg-gray-400 dark:bg-zinc-600';
    if (bridge.connected) return 'bg-emerald-400 shadow-lg shadow-emerald-400/30';
    return 'bg-red-400 shadow-lg shadow-red-400/30';
  };

  const getStatusText = () => {
    if (bridge.paused) return 'Paused';
    if (bridge.connected) return 'Connected';
    return 'Disconnected';
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
      {/* Bridge Header */}
      <div className="flex items-center justify-between p-4">
        <button
          onClick={onToggleExpand}
          className="flex flex-1 items-center gap-4 text-left"
          type="button"
        >
          <ChevronRight
            className={`h-5 w-5 text-gray-400 transition-transform dark:text-zinc-600 ${
              isExpanded ? 'rotate-90' : ''
            }`}
          />
          <div className={`h-3 w-3 rounded-full ${getStatusColor()}`} />
          <div>
            <div className="text-[15px] font-medium text-gray-900 dark:text-white">
              {bridge.name}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
              <span>
                {bridge.host}:{bridge.port}
              </span>
              <span className="text-gray-300 dark:text-zinc-700">•</span>
              <span>{getStatusText()}</span>
              {enabledDevices.length > 0 && (
                <>
                  <span className="text-gray-300 dark:text-zinc-700">•</span>
                  <span>
                    {enabledDevices.length} device{enabledDevices.length !== 1 ? 's' : ''} enabled
                  </span>
                </>
              )}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDiscover}
            disabled={discoverMutation.isPending || bridge.paused || !bridge.connected}
            title="Discover devices"
          >
            {discoverMutation.isPending ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onPauseResume}
            disabled={isPauseLoading}
            title={bridge.paused ? 'Resume connection' : 'Pause connection'}
          >
            {isPauseLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : bridge.paused ? (
              <Play className="h-4 w-4" />
            ) : (
              <Pause className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>

      {/* Expanded Content - Devices */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50 dark:border-white/[0.06] dark:bg-zinc-900/50">
          {devicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
            </div>
          ) : enabledDevices.length === 0 ? (
            <div className="p-6 text-center">
              <Lightbulb className="mx-auto h-8 w-8 text-gray-400 dark:text-zinc-600" />
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                No devices enabled yet. Click the discover button to find devices on this bridge.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
              {enabledDevices.map((device) => (
                <DeviceListItem
                  key={device.id}
                  device={device}
                  onDisable={() => setDisablingDevice(device)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Discovered Devices Modal */}
      {showDiscoveredDevices && (
        <DiscoveredDevicesModal
          isOpen={true}
          bridge={bridge}
          discoveredDevices={discoveredDevices}
          enabledDeviceIds={new Set(enabledDevices.map((d) => d.hueDeviceId ?? ''))}
          onClose={() => {
            setShowDiscoveredDevices(false);
            setDiscoveredDevices([]);
          }}
          onEnableDevice={(device) => {
            setEnablingDevice(device);
          }}
        />
      )}

      {/* Enable Device Modal */}
      {enablingDevice && (
        <EnableDeviceModal
          isOpen={true}
          bridge={bridge}
          device={enablingDevice}
          onClose={() => setEnablingDevice(null)}
          onSuccess={() => {
            setEnablingDevice(null);
            invalidateDevices();
          }}
        />
      )}

      {/* Disable Device Modal */}
      {disablingDevice && (
        <DisableDeviceModal
          isOpen={true}
          device={disablingDevice}
          onClose={() => setDisablingDevice(null)}
          onSuccess={() => {
            setDisablingDevice(null);
            invalidateDevices();
          }}
        />
      )}
    </div>
  );
}

interface DeviceListItemProps {
  device: HueDevice;
  onDisable: () => void;
}

function DeviceListItem({ device, onDisable }: DeviceListItemProps) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-3">
        <Lightbulb className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">{device.name}</div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
            <span>{device.productName}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span className="font-mono">{device.deviceType}</span>
          </div>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onDisable} title="Disable device">
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </div>
  );
}

interface DiscoveredDevicesModalProps {
  isOpen: boolean;
  bridge: HueBridge;
  discoveredDevices: HueDevice[];
  enabledDeviceIds: Set<string>;
  onClose: () => void;
  onEnableDevice: (device: HueDevice) => void;
}

function DiscoveredDevicesModal({
  isOpen,
  bridge,
  discoveredDevices,
  enabledDeviceIds,
  onClose,
  onEnableDevice,
}: DiscoveredDevicesModalProps) {
  const availableDevices = discoveredDevices.filter(
    (d) => !enabledDeviceIds.has(d.hueDeviceId ?? '')
  );

  const footerContent = (
    <Button variant="ghost" onClick={onClose}>
      Close
    </Button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Devices on ${bridge.name}`}
      subtitle="Select devices to enable for control"
      size="lg"
      footer={footerContent}
    >
      {discoveredDevices.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-zinc-500">
          No devices found on this bridge.
        </p>
      ) : availableDevices.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-zinc-500">
          All discovered devices are already enabled.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-zinc-500">
            Found {availableDevices.length} device{availableDevices.length !== 1 ? 's' : ''}{' '}
            available to enable:
          </p>
          <div className="max-h-96 divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 dark:divide-white/[0.06] dark:border-white/[0.06]">
            {availableDevices.map((device) => (
              <div key={device.hueDeviceId} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <Lightbulb className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {device.name}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
                      <span>{device.productName}</span>
                      <span className="text-gray-300 dark:text-zinc-700">•</span>
                      <span>{device.deviceType}</span>
                      <span className="text-gray-300 dark:text-zinc-700">•</span>
                      <span className="font-mono">{device.modelId}</span>
                    </div>
                  </div>
                </div>
                <Button size="sm" onClick={() => onEnableDevice(device)}>
                  Enable
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

interface EnableDeviceModalProps {
  isOpen: boolean;
  bridge: HueBridge;
  device: HueDevice;
  onClose: () => void;
  onSuccess: () => void;
}

function EnableDeviceModal({ isOpen, bridge, device, onClose, onSuccess }: EnableDeviceModalProps) {
  const createMutation = usePostApiIntegrationsPhilipsHueDevices();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<EnableDeviceFormData>({
    resolver: zodResolver(enableDeviceSchema),
    defaultValues: {
      name: device.name ?? '',
      humanId: '',
      deviceType: (device.deviceType as 'light' | 'switch') ?? 'light',
    },
  });

  const selectedDeviceType = watch('deviceType');

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = async (data: EnableDeviceFormData) => {
    setError(null);
    try {
      await createMutation.mutateAsync({
        data: {
          bridgeId: bridge.id ?? '',
          hueDeviceId: device.hueDeviceId ?? '',
          humanId: data.humanId,
          name: data.name,
          deviceType: data.deviceType,
          modelId: device.modelId ?? '',
          productName: device.productName ?? '',
          enabled: true,
        },
      });
      handleClose();
      onSuccess();
    } catch (err) {
      const apiError = err as { code?: string; message?: string };
      if (apiError.code === 'HUMAN_ID_EXISTS') {
        setError('This ID is already in use. Please choose a different one.');
      } else {
        setError(apiError.message ?? 'Failed to enable device. Please try again.');
      }
    }
  };

  const footerContent = (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={handleClose}
        disabled={createMutation.isPending}
      >
        Cancel
      </Button>
      <Button type="submit" form="enable-device-form" loading={createMutation.isPending}>
        Enable Device
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enable Device"
      subtitle={`Add "${device.name}" to your smart home`}
      size="md"
      footer={footerContent}
    >
      <form id="enable-device-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room Light"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="Device ID"
          placeholder="living-room-light"
          error={errors.humanId?.message}
          {...register('humanId')}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Device Type
          </label>
          <div className="flex gap-3">
            <label
              className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-all ${
                selectedDeviceType === 'light'
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-400'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]'
              }`}
            >
              <input type="radio" value="light" className="sr-only" {...register('deviceType')} />
              <Lightbulb className="h-4 w-4" />
              Light
            </label>
            <label
              className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-all ${
                selectedDeviceType === 'switch'
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-400'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]'
              }`}
            >
              <input type="radio" value="switch" className="sr-only" {...register('deviceType')} />
              <Power className="h-4 w-4" />
              Switch / Plug
            </label>
          </div>
          {errors.deviceType?.message && (
            <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
              {errors.deviceType.message}
            </p>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface DisableDeviceModalProps {
  isOpen: boolean;
  device: HueDevice;
  onClose: () => void;
  onSuccess: () => void;
}

function DisableDeviceModal({ isOpen, device, onClose, onSuccess }: DisableDeviceModalProps) {
  const deleteMutation = useDeleteApiIntegrationsPhilipsHueDevicesId();

  const handleConfirm = async () => {
    if (!device.id) return;
    try {
      await deleteMutation.mutateAsync({ id: device.id });
      onClose();
      onSuccess();
    } catch {
      // Error handling done by mutation
    }
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={deleteMutation.isPending}>
        Cancel
      </Button>
      <Button
        type="button"
        variant="danger"
        onClick={handleConfirm}
        loading={deleteMutation.isPending}
      >
        Disable
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Disable Device"
      subtitle="Remove device from your smart home"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to disable{' '}
        <strong className="text-gray-900 dark:text-white">{device.name}</strong>? This will remove
        it from the smart home system. You can re-enable it later by running device discovery.
      </p>
    </Modal>
  );
}

interface AddBridgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddBridgeFormData) => Promise<void>;
  onRetryAuth: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  authPending: {
    name: string;
    host: string;
    port: number;
    message: string;
    timeoutSeconds?: number;
  } | null;
}

function AddBridgeModal({
  isOpen,
  onClose,
  onSubmit,
  onRetryAuth,
  isLoading,
  error,
  authPending,
}: AddBridgeModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddBridgeFormData>({
    resolver: zodResolver(addBridgeSchema),
    defaultValues: {
      port: 443,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  // If authentication is pending, show the auth waiting screen
  if (authPending) {
    const authFooterContent = (
      <>
        <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="button" onClick={onRetryAuth} loading={isLoading}>
          Retry
        </Button>
      </>
    );

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Authenticate Bridge"
        subtitle="Press the button on your Hue bridge"
        size="md"
        footer={authFooterContent}
      >
        <div className="flex flex-col items-center justify-center py-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10">
            <Router className="h-8 w-8 text-amber-500" />
          </div>
          <p className="mt-4 text-center text-sm text-gray-500 dark:text-zinc-500">
            {authPending.message}
          </p>
          {authPending.timeoutSeconds && (
            <CountdownTimer seconds={authPending.timeoutSeconds} onRetry={onRetryAuth} />
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </Modal>
    );
  }

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="add-bridge-form" loading={isLoading}>
        Add Bridge
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Philips Hue Bridge"
      subtitle="Connect your bridge to the smart home system"
      size="md"
      footer={footerContent}
    >
      <form id="add-bridge-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room Bridge"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="IP Address"
          placeholder="192.168.1.100"
          error={errors.host?.message}
          {...register('host')}
        />

        <TextField
          label="Port"
          placeholder="443"
          type="number"
          error={errors.port?.message}
          {...register('port')}
        />

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface CountdownTimerProps {
  seconds: number;
  onRetry: () => void;
}

function CountdownTimer({ seconds, onRetry }: CountdownTimerProps) {
  const [remaining, setRemaining] = useState(seconds);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setRemaining(seconds);
    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [seconds]);

  if (remaining === 0) {
    return (
      <p className="mt-2 text-sm text-amber-500">
        Timeout expired.{' '}
        <button onClick={onRetry} className="underline hover:no-underline">
          Retry
        </button>
      </p>
    );
  }

  return (
    <p className="mt-2 text-sm text-gray-400 dark:text-zinc-600">
      Waiting... {remaining}s remaining
    </p>
  );
}

interface EditBridgeModalProps {
  isOpen: boolean;
  bridge: HueBridge;
  onClose: () => void;
  onSubmit: (data: EditBridgeFormData) => Promise<void>;
  isLoading: boolean;
}

function EditBridgeModal({ isOpen, bridge, onClose, onSubmit, isLoading }: EditBridgeModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditBridgeFormData>({
    resolver: zodResolver(editBridgeSchema),
    defaultValues: {
      name: bridge.name ?? '',
      host: bridge.host ?? '',
      port: bridge.port ?? 443,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-bridge-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Bridge"
      subtitle="Update bridge connection settings"
      size="md"
      footer={footerContent}
    >
      <form id="edit-bridge-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room Bridge"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="IP Address"
          placeholder="192.168.1.100"
          error={errors.host?.message}
          {...register('host')}
        />

        <TextField
          label="Port"
          placeholder="443"
          type="number"
          error={errors.port?.message}
          {...register('port')}
        />
      </form>
    </Modal>
  );
}

interface DeleteBridgeModalProps {
  isOpen: boolean;
  bridge: HueBridge;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteBridgeModal({
  isOpen,
  bridge,
  onClose,
  onConfirm,
  isLoading,
}: DeleteBridgeModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Bridge"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{bridge.name}</strong>? This will also
        remove all associated devices.
      </p>
    </Modal>
  );
}
